<script setup>
import { ref } from 'vue';
import { useLayoutComponents } from '@/composables/layout.js';
import { useIcons } from '@/composables/icons.js';
import { useToast } from '@/composables/use-toast.js';
import axios from '@/plugins/axios';

// Composables
const { TextInput, PrimaryButton, ButtonLoading, Toast } = useLayoutComponents();
const { Envelope } = useIcons();
const { addToast } = useToast();

// Reactive state
const email = ref('');
const message = ref('');
const loading = ref(false);

const sendUsAMessage = async () => {
  try {
    loading.value = true;
    const response = await axios.post('/api/email/send-email', {
      from: email.value,
      to: 'hello@portexai.com',
      subject: 'Message from ' + email.value,
      message: message.value,
      recipient: 'hello@portexai.com'
    });

    console.log(response);

    if (response.status === 201) {
      addToast('Your message has been sent successfully! We will get back to you soon.', 'success');
      // Clear the form after successful submission
      email.value = '';
      message.value = '';
    } else {
      addToast('Failed to send your message. Please try again.', 'error');
    }
  } catch (error) {
    addToast(error.response?.data?.message || 'Failed to send your message. Please try again.', 'error');
  } finally {
    loading.value = false;
  }
};
</script>

<template>
  <Toast />
  <div class="send-us-a-message">
    <div class="send-us-a-message-card">
      <!-- Header -->
      <div class="send-us-a-message-card__header">
        <img src="/favicon.ico" alt="logo" class="send-us-a-message-card__header-logo" />
        <h2 class="send-us-a-message-card__header-title">Send Us a Message</h2>
        <span class="send-us-a-message-card__header-subtitle">
          Send us a message and we will get back to you as soon as possible
        </span>
      </div>
      
      <!-- Body -->
      <div class="send-us-a-message-card__body">
        <form @submit.prevent="sendUsAMessage" class="send-us-a-message-card__body-form">
          <!-- Email input -->
          <div class="send-us-a-message-card__body-form-group">
            <TextInput v-model="email" type="email" id="email" placeholder="Your email address (example@email.com)" required>
              <template #icon>
                <Envelope class="w-5 h-5" />
              </template>
            </TextInput>
          </div>

          <!-- Message input -->
          <div class="send-us-a-message-card__body-form-group">
              <div class="relative mt-2">
                  <textarea 
                      v-model="message"
                      rows="3"
                      class="w-full px-4 py-2.5 text-sm rounded-[13px] bg-echoes-background-black text-white border border-echoes-border-gray placeholder-white/50 hover:placeholder-white transition-all duration-200 ease-in-out resize-none focus:outline-none focus:border-echoes-primary" 
                      placeholder="Type your message here..."
                  ></textarea>
              </div>
          </div>

          <!-- Send message button -->
          <div class="send-us-a-message-card__body-form-button h-10">
            <PrimaryButton 
              :disabled="loading || !email || message.length === 0"
              type="primary"
              class="w-full h-full"
            >
              <ButtonLoading v-if="loading" size="sm" text="Sending message..." />
              <template v-else>Send Message</template>
            </PrimaryButton>
          </div>
        </form>

        <div class="text-center">
          <router-link to="/" class="text-sm text-white/80 hover:text-echoes-primary font-semibold">
            Back to Home
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="postcss">
.send-us-a-message {
  @apply flex justify-center items-center h-[100dvh] w-screen px-4;
  background-image: linear-gradient(to bottom, #232323, #000000);
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
}

.send-us-a-message-card {
  @apply w-full max-w-sm rounded-2xl p-4 shadow-lg bg-echoes-background-black border border-echoes-border-gray;
  backdrop-filter: blur(8px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3), 0 4px 6px rgba(0, 0, 0, 0.2);
  background-clip: padding-box;
}

.send-us-a-message-card__header {
  @apply mb-4 flex flex-col items-center justify-center gap-2;
}

.send-us-a-message-card__header-title {
  @apply text-2xl font-semibold text-white;
}

.send-us-a-message-card__header-subtitle {
  @apply text-sm text-gray-400;
}

.send-us-a-message-card__header-logo {
  @apply w-12 h-12;
}

.send-us-a-message-card__body {
  @apply space-y-4;
}

.send-us-a-message-card__body-form {
  @apply flex flex-col gap-2;
}

.send-us-a-message-card__body-form-group {
  @apply flex flex-col gap-2;
}

.send-us-a-message-card__body-form-button {
  @apply w-full shadow-lg;
}

.send-us-a-message-card__footer {
  @apply mt-6 flex justify-between;
}

.social-reset-password-buttons {
  @apply hidden justify-center gap-4;
}

.social-button {
  @apply flex items-center justify-center gap-2 w-full p-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 transition-colors shadow-md;
}
</style>